// Candidate chooser — shown only when turn 1 finds more than one business,
// or renders the noVendorFound observed text with a repick CTA.
import type { ExtractionCandidate } from '@stash/shared-types';

interface CandidateChooserProps {
  candidates: ExtractionCandidate[];
  observed: string | null;
  onChoose: (candidateId: string) => void;
  onRepick: () => void;
}

function candidateLabel(candidate: ExtractionCandidate): string {
  const name = candidate.fields.find((field) => field.field === 'companyName')?.value;
  return typeof name === 'string' && name ? name : candidate.candidateId;
}

export function CandidateChooser({ candidates, observed, onChoose, onRepick }: CandidateChooserProps): React.JSX.Element {
  if (candidates.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 py-6 text-center">
        <p className="text-sm text-muted-foreground max-w-sm">
          {observed ?? 'We couldn’t spot a business in that photo.'}
        </p>
        <button
          type="button"
          onClick={onRepick}
          className="rounded-full bg-secondary px-5 py-2.5 font-semibold text-secondary-foreground hover:bg-[var(--stash-blue-light)]"
        >
          Try another photo
        </button>
      </div>
    );
  }
  return (
    <ul className="flex flex-col gap-2 py-4">
      {candidates.map((candidate) => (
        <li key={candidate.candidateId}>
          <button
            type="button"
            onClick={() => onChoose(candidate.candidateId)}
            className="w-full rounded-2xl border border-border bg-card px-4 py-3 text-left font-semibold text-foreground transition-colors hover:bg-[var(--stash-blue-light)]"
          >
            {candidateLabel(candidate)}
            <span className="block text-xs font-medium text-muted-foreground">
              {candidate.fields.length} field{candidate.fields.length === 1 ? '' : 's'} found
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
